import { useTranslation } from 'react-i18next';
import type { Project } from '@interfaces/IProject';
import { TrendDownIcon, TrendUpIcon } from '@utils/icons';
import { SectionHead } from '../SectionHead/SectionHead';
import { MetricBar } from '../MetricBar/MetricBar';
import { useSiteProductivity } from './useSiteProductivity';
import { onTimePct, onTimeTone, productivityTone, progressGap, progressTone, workforceBar } from './performanceRules';
import styles from './styles.module.css';

type IPerformanceProps = { project: Project };

/** Schedule progress against plan; empty when no schedule came back. */
function ProgressMetric({ project }: IPerformanceProps) {
  const { t } = useTranslation();
  const label = t('portfolio.card.progress.title');
  if (project.scheduleStatus === 'noData') {
    return <MetricBar testId="metric-progress" label={label} context={t('portfolio.card.performance.noSchedule')} fillPct={null} tone="neutral" />;
  }
  const gap = progressGap(project.actual, project.plan);
  const delta = gap < 0 ? 'behind' : gap > 0 ? 'ahead' : 'onPlan';
  return (
    <MetricBar
      testId="metric-progress"
      label={label}
      value={`${project.actual}%`}
      context={t(`portfolio.card.progress.${delta}`, { count: Math.abs(gap) })}
      contextTone={gap < 0 ? 'bad' : gap > 0 ? 'good' : undefined}
      fillPct={project.actual}
      tickPct={project.todayPct}
      tone={progressTone(project.actual, project.plan)}
      hint={t('portfolio.card.progress.hint', { plan: project.plan })}
    />
  );
}

/** The site's productivity, as its Productivity page shows it. */
function ProductivityMetric({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const query = useSiteProductivity(siteId);
  const label = t('portfolio.card.productivity.label');
  if (!query.data) {
    return <MetricBar testId="metric-productivity" label={label} value={query.isError ? '—' : '…'} fillPct={null} tone="neutral" />;
  }
  const { headline, coverage } = query.data;
  const hint = t('portfolio.card.productivity.hint', { measured: coverage.measured, total: coverage.total });
  if (!headline?.value || headline.value === '—') {
    return <MetricBar testId="metric-productivity" label={label} context={t('portfolio.card.productivity.notMeasured')} fillPct={null} tone="neutral" hint={hint} />;
  }
  const TrendIcon = headline.trend === 'down' ? TrendDownIcon : TrendUpIcon;
  return (
    <MetricBar
      testId="metric-productivity"
      label={label}
      value={headline.value}
      context={
        headline.trend ? (
          <span className={styles.trend}>
            <TrendIcon size={10} />
            {headline.trendValue}
          </span>
        ) : undefined
      }
      contextTone={headline.trend === 'down' ? 'bad' : headline.trend === 'up' ? 'good' : undefined}
      fillPct={Number.parseFloat(headline.value)}
      tone={productivityTone(headline.tone)}
      hint={hint}
    />
  );
}

/** Of the tasks due over the last 14 days, the share finished on time. */
function OnTimeMetric({ project }: IPerformanceProps) {
  const { t } = useTranslation();
  const label = t('portfolio.card.onTime.title');
  const tasks = project.onTimeTasks;
  if (!tasks) return <MetricBar testId="metric-on-time" label={label} value="—" fillPct={null} tone="neutral" />;
  const pct = onTimePct(tasks);
  if (pct === null) {
    return <MetricBar testId="metric-on-time" label={label} context={t('portfolio.card.onTime.noneDue')} fillPct={null} tone="neutral" />;
  }
  return (
    <MetricBar
      testId="metric-on-time"
      label={label}
      value={`${pct}%`}
      context={`${tasks.onTime}/${tasks.due}`}
      fillPct={pct}
      tone={onTimeTone(pct)}
      hint={t('portfolio.card.onTime.hint', { onTime: tasks.onTime, due: tasks.due })}
    />
  );
}

/** Who is on site now, against the planned daily workforce when there is one. */
function WorkforceMetric({ project }: IPerformanceProps) {
  const { t, i18n } = useTranslation();
  const label = t('portfolio.card.meta.workforce');
  const value = project.workers.toLocaleString(i18n.language);
  const expected = project.expectedWorkforce;
  if (expected === null) {
    return <MetricBar testId="metric-workforce" label={label} value={value} context={t('portfolio.card.meta.live')} fillPct={null} tone="neutral" />;
  }
  const bar = workforceBar(project.workers, expected);
  return (
    <MetricBar
      testId="metric-workforce"
      label={label}
      value={value}
      context={t('portfolio.card.meta.expected', { count: expected, formatted: expected.toLocaleString(i18n.language) })}
      separator=" "
      fillPct={bar.fillPct}
      tickPct={bar.tickPct}
      band={bar.band}
      tone={bar.tone}
    />
  );
}

/** The card's Performance section: four figures as small bars. */
export function Performance({ project }: IPerformanceProps) {
  const { t } = useTranslation();
  return (
    <section className={styles.section} data-testid="performance">
      <SectionHead title={t('portfolio.card.performance.title')} />
      <div className={styles.grid}>
        <ProgressMetric project={project} />
        <ProductivityMetric siteId={project.id} />
        <OnTimeMetric project={project} />
        <WorkforceMetric project={project} />
      </div>
    </section>
  );
}
